import type { Sprint, TablesInsert } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

// Cycles' data: a game's sprints, their burndown and velocity (worked out in
// the database from the sprint event log), and closing or reopening a sprint.

export type CycleSprint = Omit<Sprint, 'search'>;

const SPRINT_COLUMNS =
  'id, game_id, number, name, goal, start_date, end_date, closed_at, review, review_author_kind, review_on_behalf_of, review_updated_at, created_at, updated_at, created_by';

/** The studio's time zone for sprint days: this device's. */
export function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** A game's sprints, newest first. */
export function useSprints(gameId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.gameSprints(gameId ?? ''),
    enabled: !!gameId,
    queryFn: async (): Promise<CycleSprint[]> => {
      const { data, error } = await supabase()
        .from('sprints')
        .select(SPRINT_COLUMNS)
        .eq('game_id', gameId!)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data as CycleSprint[];
    },
  });
}

export function useCreateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      sprint: Pick<TablesInsert<'sprints'>, 'game_id' | 'start_date' | 'end_date' | 'name' | 'goal'>,
    ) => {
      const { data, error } = await supabase().from('sprints').insert(sprint).select(SPRINT_COLUMNS).single();
      if (error) throw error;
      return data as CycleSprint;
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.sprints }),
  });
}

export type SprintPatch = Partial<Pick<CycleSprint, 'name' | 'goal' | 'start_date' | 'end_date' | 'review'>>;

export function useUpdateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: SprintPatch }) => {
      const { error } = await supabase().from('sprints').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sprints }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sprintStats }),
      ]),
  });
}

export function useDeleteSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('sprints').delete().eq('id', id);
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sprints }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
      ]),
  });
}

/** Closes a sprint, moving its unfinished tasks to `carryTo` (another sprint) or the backlog. */
export function useCloseSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, carryTo }: { id: string; carryTo: string | null }) => {
      const { data, error } = await supabase().rpc('close_sprint', {
        p_sprint_id: id,
        ...(carryTo ? { p_carry_to: carryTo } : {}),
      });
      if (error) throw error;
      return data;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sprints }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sprintStats }),
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks }),
      ]),
  });
}

export function useReopenSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().rpc('reopen_sprint', { p_sprint_id: id });
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sprints }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sprintStats }),
      ]),
  });
}

export interface BurndownDay {
  day: string;
  scope_tasks: number;
  done_tasks: number;
  scope_hours: number;
  done_hours: number;
  unestimated_tasks: number;
}

export function useBurndown(sprintId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.burndown(sprintId ?? ''),
    enabled: !!sprintId,
    queryFn: async (): Promise<BurndownDay[]> => {
      const { data, error } = await supabase().rpc('sprint_burndown', {
        p_sprint_id: sprintId!,
        p_time_zone: timeZone(),
      });
      if (error) throw error;
      return data.map((d) => ({
        ...d,
        scope_hours: Number(d.scope_hours),
        done_hours: Number(d.done_hours),
      }));
    },
  });
}

export interface VelocityRow {
  sprint_id: string;
  number: number;
  name: string;
  scope_tasks: number;
  done_tasks: number;
  done_hours: number;
  carried_tasks: number;
}

export function useVelocity(gameId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.velocity(gameId ?? ''),
    enabled: !!gameId,
    queryFn: async (): Promise<VelocityRow[]> => {
      const { data, error } = await supabase().rpc('sprint_velocity', { p_game_id: gameId!, p_last: 6 });
      if (error) throw error;
      return data.map((v) => ({ ...v, done_hours: Number(v.done_hours) }));
    },
  });
}
